/**
 * Module dependencies.
 */

import { TourData } from 'src/types/tour';
import { baseFontSize, clampCalc } from 'src/components/core/text';
import { media } from 'src/styles/media';
import FadeInAnimation from 'src/components/core/animations/fade-in';
import FadeInRightAnimation from 'src/components/core/animations/fade-in-right';
import FadeInUpAnimation from 'src/components/core/animations/fade-in-up';
import Image from 'src/components/core/image';
import RawHtml from 'src/components/core/raw-html';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = {
  blocks?: TourData['sections']['bestOf']['blocksImages'];
  className?: string;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  display: flex;
  position: relative;

  ${media.max.md`
    flex-direction: column;
    grid-row-gap: 4rem;
  `}
`;

/**
 * `GridWrapper` styled component.
 */

const GridWrapper = styled.div`
  ${media.min.md`
    display: flex;
    height: 100%;
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  ${media.min.md`
    display: grid;
    grid-template-areas:
      'image1 text text   . image3'
      'image1 .    image2 . image3'
      'image1 .    image2 . subtitle';
    grid-template-columns: 5fr 1fr 2fr 1fr 3fr;
    grid-template-rows: 6fr 0.5fr 3.5fr;
    padding: 9.8vw 0 9.8vw 4.5rem;
    width: 150vw;
  `}

  ${media.min.lg`
    padding: 7.3vw 0 5.4vw 4.5rem;
    width: 115vw;
  `}

  ${media.min.xxl`
    padding-left: calc((100vw - var(--container-max-width)) / 2 + var(--gutter));
    width: 90vw;
  `}
`;

/**
 * `Image1Wrapper` styled component.
 */

const Image1Wrapper = styled.div`
  overflow: hidden;
  position: relative;

  ${media.min.md`
    grid-area: image1;
    height: 100%;
  `}

  ${media.max.md`
    margin-bottom: 0.75rem;
    padding-bottom: 122.81%;
  `}
`;

/**
 * `TextWrapper` styled component.
 */

const TextWrapper = styled.div`
  ${media.min.md`
    align-self: center;
    grid-area: text;
    padding: 1rem 0 1rem 18%;
  `}
`;

/**
 * `TitleWrapper` styled component.
 */

const TitleWrapper = styled.h3`
  color: var(--color-dark);
  font-family: var(--font-futura-pt);
  font-size: clamp(${16 / baseFontSize}rem, ${clampCalc(16, 20)}, ${20 / baseFontSize}rem);
  letter-spacing: clamp(${0.4 / baseFontSize}rem, ${clampCalc(0.4, 0.5)}, ${0.5 / baseFontSize}rem);
  line-height: clamp(${25 / baseFontSize}rem, ${clampCalc(25, 30)}, ${30 / baseFontSize}rem);
  margin-bottom: 0.5rem;
`;

/**
 * `DescriptionWrapper` styled component.
 */

const DescriptionWrapper = styled.p`
  color: var(--color-grey-500);
  font-family: var(--font-futura-pt);
  font-size: 1rem;
  letter-spacing: 0.025rem;
  line-height: clamp(${20 / baseFontSize}rem, ${clampCalc(20, 22)}, ${22 / baseFontSize}rem);

  ${media.max.md`
    margin-bottom: 1.875rem;
  `}
`;

/**
 * `Image2Wrapper` styled component.
 */

const Image2Wrapper = styled.div`
  overflow: hidden;
  position: relative;

  ${media.min.md`
    grid-area: image2;
    height: 100%;
  `}

  ${media.max.md`
    margin-bottom: 1.75rem;
    padding-bottom: 122.81%;
  `}
`;

/**
 * `Image3Wrapper` styled component.
 */

const Image3Wrapper = styled.div`
  overflow: hidden;
  position: relative;

  img {
    border: 0.625rem solid var(--color-dark) !important;
  }

  ${media.min.md`
    grid-area: image3;
    height: 100%;
  `}

  ${media.max.md`
    padding-bottom: 122.81%;
  `}
`;

/**
 * `TourSubtitleWrapper` styled component.
 */

const TourSubtitleWrapper = styled.div`
  ${media.min.md`
   align-self: center;
   grid-area: subtitle;
   padding: 1.5rem 0;
 `}
`;

/**
 * `TourSubtitle` styled component.
 */

const TourSubtitle = styled.p`
  color: var(--color-grey-500);
  font-family: var(--font-futura-pt);
  font-size: 0.94rem;
  line-height: 1.875rem;
`;

/**
 * `BlocksImagesBlock` component.
 */

const BlocksImagesBlock = ({ blocks, className }: Props) => {
  const isMobile = useBreakpoint('md', 'max');

  if (isEmpty(blocks)) return null;

  return (
    <Wrapper className={className}>
      {map(blocks, ({ description, image1, image2, image3, subtitle, title }, index: number) => (
        <GridWrapper key={index}>
          <Grid key={index}>
            <Image1Wrapper>
              <FadeInAnimation fullHeight options={{ transitionTime: 1 }}>
                <Image alt={title} layout={'fill'} objectFit={'cover'} src={image1} />
              </FadeInAnimation>
            </Image1Wrapper>

            <TextWrapper>
              {!isMobile && (
                <FadeInRightAnimation options={{ transitionTime: 1 }}>
                  {title && <TitleWrapper>{title}</TitleWrapper>}

                  {description && <RawHtml element={DescriptionWrapper}>{description}</RawHtml>}
                </FadeInRightAnimation>
              )}

              {isMobile && (
                <FadeInUpAnimation options={{ transitionTime: 1 }}>
                  {title && <TitleWrapper>{title}</TitleWrapper>}

                  {description && <RawHtml element={DescriptionWrapper}>{description}</RawHtml>}
                </FadeInUpAnimation>
              )}
            </TextWrapper>

            <Image2Wrapper>
              <FadeInAnimation fullHeight options={{ transitionDelay: 0.2, transitionTime: 1 }}>
                <Image alt={title} layout={'fill'} objectFit={'cover'} src={image2} />
              </FadeInAnimation>
            </Image2Wrapper>

            <Image3Wrapper>
              <FadeInAnimation fullHeight options={{ transitionTime: 1 }}>
                <Image alt={title} layout={'fill'} objectFit={'cover'} src={image3} />
              </FadeInAnimation>
            </Image3Wrapper>

            <TourSubtitleWrapper>
              {!isMobile && (
                <FadeInRightAnimation options={{ transitionTime: 1 }}>
                  <TourSubtitle>{subtitle}</TourSubtitle>
                </FadeInRightAnimation>
              )}

              {isMobile && (
                <FadeInUpAnimation options={{ transitionTime: 1 }}>
                  <TourSubtitle>{subtitle}</TourSubtitle>
                </FadeInUpAnimation>
              )}
            </TourSubtitleWrapper>
          </Grid>
        </GridWrapper>
      ))}
    </Wrapper>
  );
};

/**
 * Export `BlocksImagesBlock` component.
 */

export default BlocksImagesBlock;
